// START IMPORTS
import {
  AppConfig,
  AppConstants,
  AppRequests,
  CollectionsCatalogProvider,
  createAppConfig,
  useCanAccess,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
import { styled } from '@ltvco/refresh-lib/theme';
import {
  getSearchTypeForMenuItem,
  ResponseError,
} from '@ltvco/refresh-lib/utils';
import {
  AlertProvider,
  type SideMenuItem,
  useSession,
} from '@ltvco/refresh-lib/v2';
import { useCallback, useMemo } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
  Link,
} from 'react-router-dom';

import {
  constants,
  sideMenuItemsForNewDesign,
  internationalSideMenuItems,
} from 'appConstants';
import { appRequests } from 'appRequests';
import { getInternationalPhoneSearchLocale } from 'internationalPhoneInputConfig';
import { useLimitedPlanInfo } from 'utils/useLimitedPlanInfo';
// END IMPORTS

// START STYLED_COMPONENTS
const MenuItem = styled(Link)<{ ga4EventType?: string }>(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: theme.typography.fontSize * 1.125,
  display: 'flex',
  alignItems: 'center',
  svg: {
    marginRight: theme.spacing(1),
  },
  width: '100%',
}));
// END STYLED_COMPONENTS

export function LibContextWrapper({
  children,
  trackEventGA4,
  trackEvent,
  logError,
  handleLogoutClick,
  isInInternationalLanguageTest,
  isInInternationalVinTest,
  isInOwnerSearchVariation,
  isInSingleReportVariation,
  isInAstrologyStandaloneVariation,
}: {
  children: React.ReactNode | React.ReactNode[];
  // eslint-disable-next-line no-unused-vars
  trackEventGA4: (eventToTrack: object, eventName?: string) => void;
  // eslint-disable-next-line no-unused-vars
  trackEvent: (category: string, action: string, label?: string) => void;
  logError: (
    // eslint-disable-next-line no-unused-vars
    context: string,
    // eslint-disable-next-line no-unused-vars
    error: Error | ResponseError,
    // eslint-disable-next-line no-unused-vars
    url: string | undefined
  ) => void;
  handleLogoutClick: () => void;
  isInInternationalLanguageTest: boolean;
  isInInternationalVinTest: boolean;
  isInOwnerSearchVariation: boolean;
  isInSingleReportVariation: boolean;
  isInAstrologyStandaloneVariation: boolean;
}) {
  // START HOOKS_AND_QUERIES
  const location = useLocation();
  const navigate = useNavigate();
  const {
    session: { account },
  } = useSession();
  const { phoneOnlyVariation, isBlocked, fraudScanOnlyVariation } =
    useSubscriptionAccessConfigContext();
  const { isSeatManager } = useLimitedPlanInfo();
  // END HOOKS_AND_QUERIES

  // START DERIVED_DATA_AND_UTILS
  const defaultConfig = createAppConfig();
  const isUKUser =
    account?.account?.subscription_info?.user_country === 'UK' ||
    window.location.hostname.endsWith('.co.uk');

  function stripMemberPerksFromSideMenu(
    menuItems: SideMenuItem[]
  ): SideMenuItem[] {
    return menuItems
      .filter((item) => item.name !== 'memberPerks')
      .map((item) =>
        item.childItems?.length
          ? {
              ...item,
              childItems: stripMemberPerksFromSideMenu(item.childItems),
            }
          : item
      );
  }

  function flattenMenuItems(menuItems: SideMenuItem[]): SideMenuItem[] {
    return menuItems.flatMap(({ childItems, ...item }) => [
      item,
      ...(childItems?.length ? flattenMenuItems(childItems) : []),
    ]);
  }

  const getSubscriptionSearchTypeForMenuItem = (
    menuItem: SideMenuItem
  ): string =>
    getSearchTypeForMenuItem(menuItem.name, menuItem.route, menuItem.text) ??
    '';

  const flatMenu = flattenMenuItems(
    constants.config.sideMenuItems.filter((item) => {
      if (item.name === 'importList') {
        return false;
      }
      if (
        isInSingleReportVariation &&
        isBlocked(getSubscriptionSearchTypeForMenuItem(item))
      ) {
        return false;
      }
      if (!isInOwnerSearchVariation && item.name === 'owner_search') {
        return false;
      }
      if (!isInAstrologyStandaloneVariation && item.name === 'astrology') {
        return false;
      }
      if (!isSeatManager && item.name === 'planMembers') {
        return false;
      }
      if (
        fraudScanOnlyVariation &&
        (item.name === 'dashboard' ||
          item.name === 'more-searches' ||
          item.name === 'memberPerks')
      ) {
        return false;
      }
      return true;
    })
  );

  const menuItems = flatMenu.map((item) => {
    return (
      <MenuItem
        to={item.route}
        data-cy={`menu_${item['data-cy']}`}
        ga4EventType={item.ga4EventType}
      >
        {item.icon} {item.text}
      </MenuItem>
    );
  });

  const languageSideMenuItems = ['personal-growth', 'my-account'];
  const memoizedItems = useMemo(() => {
    const items = sideMenuItemsForNewDesign(
      isInAstrologyStandaloneVariation,
      isSeatManager
    );
    if (!isUKUser) {
      return items;
    }
    return items
      .filter((item) => item.name !== 'myGarage')
      .map((item) =>
        item.childItems
          ? {
              ...item,
              childItems: item.childItems.filter((c) => c.name !== 'myGarage'),
            }
          : item
      );
  }, [isInAstrologyStandaloneVariation, isSeatManager, isUKUser]);

  let filterSideMenuItems;

  if (isInInternationalVinTest) {
    filterSideMenuItems = internationalSideMenuItems;
  } else if (isInInternationalLanguageTest) {
    filterSideMenuItems = internationalSideMenuItems.filter((item) =>
      languageSideMenuItems.includes(item.name)
    );
  } else {
    filterSideMenuItems = memoizedItems;
  }

  filterSideMenuItems = filterSideMenuItems.map((item) => {
    if (item.name === 'monitoring-hub' && item.childItems) {
      return {
        ...item,
        childItems: item.childItems.map((childItem) => {
          if (childItem.name === 'idMonitor') {
            return {
              ...childItem,
              text: 'Personal Reports',
              route: '/dashboard/personal-reports',
            };
          }
          return childItem;
        }),
      };
    }

    // Filter out ID Monitor and referrals for phone-only variation users
    // Same logic used for hiding court cases CTA from sidebar
    if (item.childItems && phoneOnlyVariation) {
      return {
        ...item,
        childItems: item.childItems.filter(
          (childItem) =>
            childItem.name !== 'referrals' && childItem.name !== 'idMonitor'
        ),
      };
    }
    return item;
  });

  if (fraudScanOnlyVariation) {
    filterSideMenuItems = filterSideMenuItems.filter(
      (item) => item.name !== 'dashboard' && item.name !== 'more-searches'
    );
    filterSideMenuItems = stripMemberPerksFromSideMenu(filterSideMenuItems);
  }

  function trackListhub(eventName: string, listingKey: string) {
    const listhubFn = (window as any).lh;
    if (listhubFn && typeof listhubFn !== 'undefined') {
      if (eventName && listingKey) {
        listhubFn('submit', eventName || null, { lkey: listingKey || null });
      }
    }
  }

  const appConfig = {
    ...defaultConfig,
    routingUtils: {
      ...defaultConfig.routingUtils,
      navigate,
      location,
      useSearchParams,
      useLocation: () => location,
      useParams,
    },
    logError,
    trackEvent,
    trackEventGA4,
    trackListhub,
  };

  const brandLogo =
    isUKUser && constants.images.UKBrandLogo
      ? constants.images.UKBrandLogo
      : constants.images.brandLogo;

  const tosLink =
    isUKUser && constants.links.UKTosLink
      ? constants.links.UKTosLink
      : constants.links.tosLink;

  const privacyLink =
    isUKUser && constants.links.UKPrivacyLink
      ? constants.links.UKPrivacyLink
      : constants.links.privacyLink;

  const handleLogoutMenuClick = useCallback(() => {
    handleLogoutClick();
  }, [handleLogoutClick]);

  const { canAccess: crPhoneSearch } = useCanAccess('cr_phone_search');
  const { canAccess: ukPhoneSearch } = useCanAccess('uk_phone_search');
  const internationalPhoneInput = useMemo(() => {
    const { countryCodes, placeholders } = getInternationalPhoneSearchLocale({
      crPhoneSearch,
      ukPhoneSearch,
    });
    return {
      ...constants.internationalPhoneInput,
      phoneSearchCountryCodes: countryCodes,
      phoneSearchPlaceholders: placeholders,
    };
  }, [crPhoneSearch, ukPhoneSearch]);

  const appConstants = useMemo(
    () => ({
      ...constants,
      internationalPhoneInput,
      images: {
        ...constants.images,
        brandLogo,
      },
      links: {
        ...constants.links,
        tosLink,
        privacyLink,
      },
      config: {
        ...constants.config,
        menuItems: [
          ...menuItems,
          <MenuItem
            to="#"
            onClick={handleLogoutMenuClick}
            data-cy="menu_log_out_btn"
          >
            Log Out
          </MenuItem>,
        ],
        sideMenuItems: [
          ...filterSideMenuItems,
          {
            route: '',
            icon: '',
            text: 'Logout',
            name: 'logout',
            state: '',
            'data-cy': 'menu_log_out_btn',
            action: handleLogoutMenuClick,
          },
        ],
      },
    }),
    [
      brandLogo,
      filterSideMenuItems,
      handleLogoutMenuClick,
      menuItems,
      internationalPhoneInput,
      tosLink,
      privacyLink,
    ]
  );
  // END DERIVED_DATA_AND_UTILS

  // START RENDER
  return (
    <AlertProvider maxSnack={3} preventDuplicate autoHideDuration={5000}>
      <AppRequests.Provider value={appRequests}>
        <AppConfig.Provider value={appConfig as any}>
          <AppConstants.Provider value={appConstants}>
            {/* Inside AppConstants so it can re-provide structuredCollections
                from gdt-search-api, and above everything so the components that
                read the catalog find it already in cache. */}
            <CollectionsCatalogProvider>{children}</CollectionsCatalogProvider>
          </AppConstants.Provider>
        </AppConfig.Provider>
      </AppRequests.Provider>
    </AlertProvider>
  );
  // END RENDER
}
